import * as React from "react";

import MuiPhoneNumber from "material-ui-phone-number";

import { useMerchantModel } from "client/hooks";
import { CountryList, Field, ValueOf } from "client/lib/apt-merchant";
import { PhoneField as PhoneFieldClass } from "client/lib/apt-merchant/src/fields/storeDetails/PhoneField";
import { countryPhoneMap } from "client/lib/apt-merchant/src/utils/countryPhoneMap";

interface InputFieldProps {
  field: PhoneFieldClass;
  hasError: boolean;
  handleChange: (field: Field<any>, value: ValueOf<typeof field>) => void;
  helperText?: string;
}

export const PhoneField: React.FC<InputFieldProps> = ({
  field,
  handleChange,
  hasError,
  helperText,
}) => {
  const { merchant, updatePhoneCountryValidationProperty } = useMerchantModel();
  const [localStateFieldValue, setLocalStateFieldValue] = React.useState(
    field?.value ?? ""
  );

  /*
   * In order to validate phone number based on its country formatting, we needed to grab
   * the name of the country to perform a look up for the associated format. To grab the
   * current country name: as the user is typing an input MUIPhoneNumber component returns
   * from the onChange event a country object { name, dialCode, countrycode }
   * (reference https://github.com/alexplumb/material-ui-phone-number) which we're capturing in
   * the event handler and saving locally here as well as updating the phoneCountryNameForValidation
   * property on the PhoneField class to be passed into its validate method
   */
  const [phoneCountryName, setPhoneCountryName] = React.useState("");

  React.useEffect(() => {
    if (phoneCountryName !== field.phoneCountryNameForValidation) {
      updatePhoneCountryValidationProperty(field, phoneCountryName);
    }
  }, [phoneCountryName, field, updatePhoneCountryValidationProperty]);

  React.useEffect(() => {
    setLocalStateFieldValue(field.value || "");
  }, [field.value]);

  const updateField = (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement>
  ) => {
    e.preventDefault();
    if (e.currentTarget.value !== field.value) {
      handleChange(field, localStateFieldValue);
    }
  };

  const getDefaultCountry = (country: CountryList | undefined) => {
    if (!country) {
      return undefined;
    }
    return countryPhoneMap[country].iso2;
  };

  const formatPhoneNumber = (phoneNumber: string) =>
    phoneNumber.replace(/[^0-9+]/g, "");

  const handleAutoformat = (e: string): void => {
    const formattedNumber = formatPhoneNumber(e);
    setLocalStateFieldValue(formattedNumber);
  };

  return (
    <MuiPhoneNumber
      autoFormat={true}
      className="fs-mask"
      defaultCountry={getDefaultCountry(merchant.lead.registeredCountry.value)}
      error={hasError}
      fullWidth
      helperText={helperText}
      id={field.propertyName}
      inputProps={{
        "data-testid": "phone-field",
        ...field.uiInputBaseProps,
      }}
      label={field.i18nValues.label}
      onBlur={(e) => {
        updateField(e as React.FocusEvent<HTMLInputElement>);
      }}
      /*
       * Typescript is throwing an error because the type declaration file
       * for material-ui-phone-number is missing the country parameter
       * for the onChange even though we've verified that the country parameter is
       * available and is mentioned in the documentation.
       * https://github.com/alexplumb/material-ui-phone-number/blob/master/index.d.ts
       */
      // @ts-ignore
      onChange={(e: any, country: any) => {
        handleAutoformat(e);
        setPhoneCountryName(country.name);
      }}
      onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          updateField(e);
        }
      }}
      placeholder={field.i18nValues.placeholder}
      preferredCountries={["us", "au", "nz", "ca", "gb"]}
      value={localStateFieldValue}
      variant="outlined"
    />
  );
};
